import React, { useEffect, useRef, useState } from "react"
import { Chart, registerables } from "chart.js"
import zoomPlugin from "chartjs-plugin-zoom"
import { Analytics } from "../../Apis/analyticsApi"
import { Spin, DatePicker } from "antd"
import dayjs from "dayjs"
import { translations } from "../../utils/Translation/staticTranslation"

// Register chart.js components and plugins
Chart.register(...registerables, zoomPlugin)

const { RangePicker } = DatePicker

const AreaChartWithZoom = ({ Title, selectedStore }) => {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)
  const [loading, setLoading] = useState(false)
  const [allData, setAllData] = useState({
    months: [],
    weeks: [],
    days: [],
  })
  const [dateRange, setDateRange] = useState("last7days")
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
  })

  // Prevent duplicate API calls
  const fetchInProgress = useRef(false)
  const prevParamsRef = useRef(null)

  const fetchAllData = async (
    filterType = "last7days",
    startDate = null,
    endDate = null
  ) => {
    // Prevent duplicate calls
    const currentParams = JSON.stringify({
      storeId: selectedStore?.id,
      filterType,
      startDate,
      endDate,
    })

    if (fetchInProgress.current || prevParamsRef.current === currentParams) {
      return
    }

    fetchInProgress.current = true
    prevParamsRef.current = currentParams
    setLoading(true)

    try {
      // Send filter parameters to the backend
      const response = await Analytics.getGraph(selectedStore.id, {
        filterType,
        startDate,
        endDate,
      })

      if (response.status === 200) {
        // Process the data based on the response format
        const processedData = processApiResponse(response.data)
        setAllData(processedData)
      }
    } catch (error) {
      console.error("Error:", error)
      prevParamsRef.current = null // Reset on error to allow retry
    } finally {
      setLoading(false)
      fetchInProgress.current = false
    }
  }

  // Function to process API response into the format needed for the chart
  const processApiResponse = (data) => {
    const result = {
      months: [],
      weeks: [],
      days: [],
    }

    // Process days data if available
    if (data.days && Array.isArray(data.days)) {
      result.days = data.days.map((day) => {
        // Parse the date from the day field
        const date = new Date(day.day)
        return {
          fullDate: day.day, // Store the full date string
          day: date.getDate(), // Extract just the day number
          month: date.toLocaleString("default", { month: "short" }),
          clicks: day.total_clicks || 0,
          impressions: day.total_impressions || 0,
        }
      })
    }

    // Group days into weeks if needed
    if (result.days.length > 0) {
      result.weeks = groupDaysIntoWeeks(result.days)
    }

    // Group days into months if needed
    if (result.days.length > 0) {
      result.months = groupDaysIntoMonths(result.days)
    }

    return result
  }

  // Group days into weeks
  const groupDaysIntoWeeks = (days) => {
    const weeks = []
    let currentWeek = []

    days.forEach((day, index) => {
      currentWeek.push(day)

      // Create a new week after every 7 days or at the end
      if ((index + 1) % 7 === 0 || index === days.length - 1) {
        const totalClicks = currentWeek.reduce(
          (sum, day) => sum + day.clicks,
          0
        )
        const totalImpressions = currentWeek.reduce(
          (sum, day) => sum + day.impressions,
          0
        )

        // Get the start and end dates of the week
        const startDay = currentWeek[0]
        const endDay = currentWeek[currentWeek.length - 1]
        const weekLabel = `${startDay.day} ${startDay.month} - ${endDay.day} ${endDay.month}`

        weeks.push({
          week: weekLabel,
          clicks: totalClicks,
          impressions: totalImpressions,
        })

        currentWeek = []
      }
    })

    return weeks
  }

  // Group days into months
  const groupDaysIntoMonths = (days) => {
    // Create a map to store month data
    const monthsMap = {}

    // Aggregate day data into months
    days.forEach((day) => {
      const date = new Date(day.fullDate)
      const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`
      const monthName = date.toLocaleString("default", {
        month: "long",
        year: "numeric",
      })

      if (!monthsMap[monthKey]) {
        monthsMap[monthKey] = {
          name: monthName,
          clicks: 0,
          impressions: 0,
        }
      }

      monthsMap[monthKey].clicks += day.clicks
      monthsMap[monthKey].impressions += day.impressions
    })

    return Object.values(monthsMap)
  }

  useEffect(() => {
    if (selectedStore && selectedStore.id) {
      fetchAllData(dateRange)
    }
  }, [selectedStore?.id])

  // Function to render the chart based on the current date range
  const renderChart = () => {
    if (!chartRef.current) return

    // Determine which data set to use based on the date range
    let data = []
    let labels = []

    if (
      allData.days.length === 0 &&
      allData.weeks.length === 0 &&
      allData.months.length === 0
    ) {
      // Handle empty data case
      if (chartInstance.current) {
        chartInstance.current.destroy()
        chartInstance.current = null
      }

      // Draw an empty chart with a message
      const ctx = chartRef.current.getContext("2d")
      chartInstance.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels: ["No data available"],
          datasets: [
            {
              label: "No data for selected date range",
              data: [0],
              backgroundColor: "rgba(200, 200, 200, 0.3)",
              borderColor: "rgba(200, 200, 200, 0.5)",
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: "top",
            },
            tooltip: {
              enabled: false,
            },
          },
        },
      })
      return
    }

    // Determine which data to use based on date range duration
    if (dateRange === "customdate") {
      // Calculate the duration between start and end dates
      if (customDateRange.startDate && customDateRange.endDate) {
        const start = new Date(customDateRange.startDate)
        const end = new Date(customDateRange.endDate)
        const durationInDays = Math.round((end - start) / (1000 * 60 * 60 * 24))

        if (durationInDays > 60) {
          // For ranges longer than 60 days, use months
          data = allData.months
          labels = data.map((item) => item.name)
        } else if (durationInDays > 7) {
          // For ranges between 8-60 days, use weeks
          data = allData.weeks
          labels = data.map((item) => item.week)
        } else {
          // For 7 days or fewer, use days
          data = allData.days
          labels = data.map((item) => `${item.day} ${item.month}`)
        }
      }
    } else if (dateRange === "last7days") {
      // For last 7 days, use daily data
      data = allData.days
      labels = data.map((item) => `${item.day} ${item.month}`)
    } else if (dateRange === "last30days" || dateRange === "currentMonth") {
      // For 30 days or current month, use weekly data
      data = allData.weeks
      labels = data.map((item) => item.week)
    } else {
      // For yearly or other longer periods, use monthly data
      data = allData.months
      labels = data.map((item) => item.name)
    }

    const clicksData = data.map((item) => item.clicks)
    const impressionsData = data.map((item) => item.impressions)

    const totalClicks = clicksData.reduce((sum, value) => sum + value, 0)
    const totalImpressions = impressionsData.reduce(
      (sum, value) => sum + value,
      0
    )

    // Set order based on total values
    const clicksOrder = totalClicks <= totalImpressions ? 1 : 2
    const impressionsOrder = totalImpressions < totalClicks ? 1 : 2

    const ctx = chartRef.current.getContext("2d")

    if (chartInstance.current) {
      chartInstance.current.destroy()
    }

    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: labels,
        datasets: [
          {
            label: dashboard?.impressions,
            data: impressionsData,
            backgroundColor: "rgba(54, 162, 235, 0.7)", // Blue for impressions
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 1,
            borderRadius: 4,
            barPercentage: 0.6,
            categoryPercentage: 0.8,
            order: impressionsOrder,
          },
          {
            label: dashboard?.clicks,
            data: clicksData,
            backgroundColor: "rgba(255, 99, 132, 0.7)", // Red for clicks
            borderColor: "rgba(255, 99, 132, 1)",
            borderWidth: 1,
            borderRadius: 4,
            barPercentage: 0.6,
            categoryPercentage: 0.8,
            order: clicksOrder,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return value >= 1000 ? `${value / 1000}k` : value
              },
            },
          },
          x: {
            grid: { display: true, drawBorder: true },
          },
        },
        plugins: {
          legend: {
            display: true,
            position: "top",
          },
          tooltip: {
            mode: "index",
            intersect: false,
            callbacks: {
              label: function (context) {
                let label = context.dataset.label || ""
                if (label) label += ": "
                label += context.parsed.y.toLocaleString()
                return label
              },
            },
          },
        },
        layout: { padding: { top: 10 } },
      },
    })
  }

  useEffect(() => {
    if (allData.days.length > 0 || allData.months.length > 0) {
      // Only render the chart if we have both dates for custom range
      if (
        dateRange === "customdate" &&
        (!customDateRange.startDate || !customDateRange.endDate)
      ) {
        return
      }
      renderChart()
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy()
      }
    }
  }, [allData, dateRange, customDateRange])

  // Handle date range change
  const handleDateRangeChange = (range) => {
    setDateRange(range)

    // If switching to custom, preserve the current custom date range if it exists
    if (range === "customdate") {
      if (customDateRange.startDate && customDateRange.endDate) {
        fetchAllData(range, customDateRange.startDate, customDateRange.endDate)
      } else {
        // Set default dates for custom range
        const endDate = new Date().toISOString().split("T")[0]
        let startDate = new Date()
        startDate.setDate(startDate.getDate() - 7)
        startDate = startDate.toISOString().split("T")[0]

        setCustomDateRange({
          startDate,
          endDate,
        })

        fetchAllData(range, startDate, endDate)
      }
    } else {
      // For non-custom ranges, just fetch with the new range type
      fetchAllData(range)
    }
  }

  // Handle custom date range change
  const handleCustomDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const startDate = dates[0].format("YYYY-MM-DD")
      const endDate = dates[1].format("YYYY-MM-DD")

      setCustomDateRange({
        startDate,
        endDate,
      })

      // Fetch data with the new date range
      fetchAllData("customdate", startDate, endDate)
    } else if (dates && dates.length === 1) {
      // When only one date is selected, update state but don't fetch yet
      setCustomDateRange((prev) => ({
        ...prev,
        startDate: dates[0].format("YYYY-MM-DD"),
      }))
    }
  }

  const lang = localStorage.getItem("language") || "ar"
  const dashboard = translations[lang]?.AnalyticsPages

  return (
    <div className="h-100">
      {loading ? (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: "15px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h4 style={{ margin: 0 }}>{Title}</h4>
              <div>
                {dateRange === "customdate" && (
                  <div
                    style={{ display: "inline-flex" }}
                    className={`${lang === "ar" ? "ml-10" : "mr-10"}`}
                  >
                    <RangePicker
                      onChange={handleCustomDateChange}
                      value={
                        customDateRange.startDate && customDateRange.endDate
                          ? [
                              dayjs(customDateRange.startDate),
                              dayjs(customDateRange.endDate),
                            ]
                          : customDateRange.startDate
                          ? [dayjs(customDateRange.startDate), null]
                          : null
                      }
                      style={{ marginRight: "15px" }}
                      placeholder={["Start Date", "End Date"]}
                      allowClear={false}
                      format="YYYY-MM-DD"
                      disabledDate={(current) => {
                        // Disable dates after today (future)
                        return current && current > dayjs().endOf("day")
                      }}
                      onOk={(dates) => {
                        // When user clicks OK, update the date range and immediately fetch data
                        if (dates && dates.length === 2) {
                          setCustomDateRange({
                            startDate: dates[0].format("YYYY-MM-DD"),
                            endDate: dates[1].format("YYYY-MM-DD"),
                          })
                        }
                      }}
                    />
                  </div>
                )}
                <select
                  value={dateRange}
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                  style={selectStyle}
                >
                  <option value="last7days">
                    {dashboard?.chart?.last_7_days}
                  </option>
                  <option value="last30days">
                    {dashboard?.chart?.last_30_days}
                  </option>
                  <option value="currentMonth">
                    {dashboard?.chart?.currentMOnth}
                  </option>
                  <option value="customdate">
                    {dashboard?.chart?.custom_range}
                  </option>
                </select>
              </div>
            </div>
          </div>
          {!selectedStore && (
            <div>
              <p>{dashboard?.select_store_to_view_analytics}</p>
            </div>
          )}
          <div style={{ position: "relative", width: "100%", height: "400px" }}>
            <canvas
              style={{ height: "400px", width: "60vw" }}
              ref={chartRef}
            ></canvas>
          </div>
        </>
      )}
    </div>
  )
}

const selectStyle = {
  padding: "8px 12px",
  fontSize: "14px",
  borderRadius: "5px",
  border: "1px solid #ddd",
  backgroundColor: "white",
  cursor: "pointer",
}
export default AreaChartWithZoom
